import React from "react";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { forEach } from "lodash";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { MrbSelectStore } from "mrb/components/select";
import { mrbDefaultFormats } from "mrb/common/formatting";
import { localizationService } from "common/localization";

class LoadioContentProfileArtistsViewStore extends MrbBaseListViewStore {
    @observable profileName = "";
    @observable assignedArtistIdSet = new Map();
    @observable statusFilter = "unassigned";
    @observable selectedArtistIds = new Map();
    @observable isBusy = false;
    @observable selectedGenreId = null;

    @computed get selectedCount() {
        return this.selectedArtistIds.size;
    }

    @computed get hasSelection() {
        return this.selectedArtistIds.size > 0;
    }

    @computed get canEnableSelected() {
        if (!this.hasSelection || this.isBusy) return false;
        for (const id of this.selectedArtistIds.keys()) {
            if (!this.assignedArtistIdSet.has(id)) return true;
        }
        return false;
    }

    @computed get canDisableSelected() {
        if (!this.hasSelection || this.isBusy) return false;
        for (const id of this.selectedArtistIds.keys()) {
            if (this.assignedArtistIdSet.has(id)) return true;
        }
        return false;
    }

    @computed get canEnableAll() {
        return !this.isBusy;
    }

    @computed get canDisableAll() {
        return !this.isBusy;
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {
                orderBy: "name",
                orderDirection: "asc",
            },
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.profileId = rootStore.routerStore.routerState.params.id;

        const filterOptions = [
            {
                label: localizationService.t(
                    "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.STATUS_FILTER.UNASSIGNED"
                ),
                value: "unassigned",
            },
            {
                label: localizationService.t(
                    "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.STATUS_FILTER.ASSIGNED"
                ),
                value: "assigned",
            },
            {
                label: localizationService.t(
                    "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.STATUS_FILTER.ALL"
                ),
                value: "all",
            },
        ];
        this.statusFilterStore = new MrbSelectStore(
            {
                isSearchable: false,
                virtualized: false,
                isClearable: false,
                textKey: "label",
                valueKey: "value",
                actions: {
                    onChange: (option) => {
                        if (option) {
                            this.setStatusFilter(option.value);
                        }
                    },
                },
            },
            filterOptions
        );
        this.statusFilterStore.setSelectedItem(filterOptions[0]);

        this.genreSelectStore = new MrbSelectStore({
            placeholder: localizationService.t(
                "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.GENRE_FILTER.PLACEHOLDER"
            ),
            isSearchable: true,
            isClearable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            actions: {
                find: (searchTerm, pageNumber, pageSize) => {
                    return this.routeStore.findGenres({
                        searchQuery: searchTerm,
                        page: pageNumber,
                        rpp: pageSize,
                        sort: "name|asc",
                    });
                },
                onChange: (option) => {
                    this.setGenreFilter(option ? option.value : null);
                },
            },
        });

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                columns: [
                    {
                        key: "name",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.COLUMNS.NAME",
                        sortable: true,
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                    },
                    {
                        key: "_status",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.COLUMNS.STATUS",
                        cell: (params) => {
                            const isAssigned = this.assignedArtistIdSet.has(params.data?.id);
                            return (
                                <span className={isAssigned ? "u-type--color--success" : "u-type--color--secondary"}>
                                    {localizationService.t(
                                        isAssigned
                                            ? "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.STATUS_BADGE.ASSIGNED"
                                            : "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.STATUS_BADGE.UNASSIGNED"
                                    )}
                                </span>
                            );
                        },
                    },
                    {
                        key: "isActive",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.COLUMNS.ACTIVE",
                        format: {
                            type: mrbDefaultFormats.bool,
                            trueLabel: "Active",
                            falseLabel: "Inactive",
                        },
                    },
                ],
                selection: {
                    rowSelection: "multiple",
                    isRowSelectable: () => true,
                    onRowSelected: (node) => {
                        const id = node.data?.id;
                        if (!id) return;
                        if (node.isSelected() && !this.selectedArtistIds.has(id)) {
                            this.toggleArtist(id, true, node.data);
                        } else if (!node.isSelected() && this.selectedArtistIds.has(id)) {
                            this.toggleArtist(id, false, node.data);
                        }
                    },
                },
                onDataChange: () => {
                    const api = this.gridStore.gridApi;
                    if (!api) return;
                    const rowsToSelect = [];
                    api.forEachNode((node) => {
                        if (node.data && this.selectedArtistIds.has(node.data.id)) {
                            rowsToSelect.push(node);
                        }
                    });
                    forEach(rowsToSelect, (node) => api.selectNode(node, true));
                },
            })
        );
    }

    async onInit() {
        const profile = await this.routeStore.getProfile(this.profileId);
        runInAction(() => {
            this.profileName = profile?.name || "";
            this.assignedArtistIdSet.clear();
            for (const id of profile?.artistIds || []) {
                this.assignedArtistIdSet.set(id, true);
            }
        });
        await super.onInit();
    }

    find(params) {
        return this.routeStore.findArtists({
            ...params,
            loadioContentProfileId: this.profileId,
            assigned: this.statusFilter === "all" ? undefined : this.statusFilter === "assigned",
            genreId: this.selectedGenreId || undefined,
        });
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error(
            localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.FETCH_ERROR"),
            err
        );
    }

    @action.bound
    setStatusFilter(value) {
        this.statusFilter = value;
        this.clearSelection();
        this.queryUtility.fetch();
    }

    @action.bound
    setGenreFilter(genreId) {
        this.selectedGenreId = genreId;
        this.clearSelection();
        this.queryUtility.fetch();
    }

    @action.bound
    toggleArtist(id, checked, item) {
        if (!id) return;
        if (checked) {
            this.selectedArtistIds.set(id, item);
        } else {
            this.selectedArtistIds.delete(id);
        }
    }

    @action.bound
    clearSelection() {
        this.selectedArtistIds.clear();
        const api = this.gridStore && this.gridStore.gridApi;
        if (api) api.deselectAll();
    }

    @action.bound
    async enableSelected() {
        const ids = Array.from(this.selectedArtistIds.keys()).filter((id) => !this.assignedArtistIdSet.has(id));
        if (ids.length === 0) return;
        runInAction(() => (this.isBusy = true));
        try {
            await this.routeStore.assignArtists(this.profileId, ids);
            runInAction(() => {
                for (const id of ids) this.assignedArtistIdSet.set(id, true);
            });
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_SUCCESS", {
                    count: ids.length,
                })
            );
            this.clearSelection();
            this.queryUtility.fetch();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_ERROR"),
                err
            );
        } finally {
            runInAction(() => (this.isBusy = false));
        }
    }

    @action.bound
    async disableSelected() {
        const ids = Array.from(this.selectedArtistIds.keys()).filter((id) => this.assignedArtistIdSet.has(id));
        if (ids.length === 0) return;
        runInAction(() => (this.isBusy = true));
        try {
            await this.routeStore.unassignArtists(this.profileId, ids);
            runInAction(() => {
                for (const id of ids) this.assignedArtistIdSet.delete(id);
            });
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_SUCCESS", {
                    count: ids.length,
                })
            );
            this.clearSelection();
            this.queryUtility.fetch();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_ERROR"),
                err
            );
        } finally {
            runInAction(() => (this.isBusy = false));
        }
    }

    @action.bound
    enableAll() {
        if (this.isBusy) return;
        const messageKey = this._hasActiveFilter()
            ? "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_ALL_MATCHING_CONFIRM"
            : "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_ALL_CONFIRM";
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t(messageKey),
            yesLabel: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_ALL_YES"),
            noLabel: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.CANCEL"),
            onConfirm: () => this._executeEnableAll(),
        });
    }

    @action.bound
    disableAll() {
        if (this.isBusy) return;
        const messageKey = this._hasActiveFilter()
            ? "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_ALL_MATCHING_CONFIRM"
            : "ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_ALL_CONFIRM";
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t(messageKey),
            yesLabel: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_ALL_YES"),
            noLabel: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.CANCEL"),
            onConfirm: () => this._executeDisableAll(),
        });
    }

    _hasActiveFilter() {
        const search = this.queryUtility && this.queryUtility.filter && this.queryUtility.filter.search;
        return Boolean(this.selectedGenreId || (search && search.trim()));
    }

    _getCurrentSearchQuery() {
        const search = this.queryUtility && this.queryUtility.filter && this.queryUtility.filter.search;
        return search && search.trim() ? search.trim() : null;
    }

    async _executeEnableAll() {
        runInAction(() => (this.isBusy = true));
        try {
            const result = await this.routeStore.enableAllArtists(this.profileId, this.selectedGenreId, this._getCurrentSearchQuery());
            const count = result?.affectedCount ?? 0;
            const profile = await this.routeStore.getProfile(this.profileId);
            runInAction(() => {
                this.assignedArtistIdSet.clear();
                for (const id of profile?.artistIds || []) {
                    this.assignedArtistIdSet.set(id, true);
                }
            });
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_SUCCESS", { count })
            );
            this.clearSelection();
            this.queryUtility.fetch();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.ENABLE_ERROR"),
                err
            );
        } finally {
            runInAction(() => (this.isBusy = false));
        }
    }

    async _executeDisableAll() {
        runInAction(() => (this.isBusy = true));
        try {
            const searchQuery = this._getCurrentSearchQuery();
            const result = await this.routeStore.disableAllArtists(this.profileId, this.selectedGenreId, searchQuery);
            const count = result?.affectedCount ?? 0;
            if (this.selectedGenreId || searchQuery) {
                // Refresh assigned set since only a filtered subset was unassigned.
                const profile = await this.routeStore.getProfile(this.profileId);
                runInAction(() => {
                    this.assignedArtistIdSet.clear();
                    for (const id of profile?.artistIds || []) {
                        this.assignedArtistIdSet.set(id, true);
                    }
                });
            } else {
                runInAction(() => this.assignedArtistIdSet.clear());
            }
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_SUCCESS", { count })
            );
            this.clearSelection();
            this.queryUtility.fetch();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.PROFILE_ARTISTS.DISABLE_ERROR"),
                err
            );
        } finally {
            runInAction(() => (this.isBusy = false));
        }
    }
}

export default LoadioContentProfileArtistsViewStore;
